import { useEffect, useState } from 'react';
import { Document, Page, pdfjs } from 'react-pdf';
import 'react-pdf/dist/Page/AnnotationLayer.css';
import 'react-pdf/dist/Page/TextLayer.css';
import {
  Pagination, Flex, Row, Column, Spacer,
} from '@oliasoft-open-source/react-ui-library';
import type { PDFDocumentProxy } from 'pdfjs-dist';
import { getRawBlob } from 'client/api-utils';
import { RecongnizebleFileFormats } from 'common/enums/known-data-enums';
import { useTranslation } from 'react-i18next';
import translations from 'client/internationalization/translation-map.json';
import { IObject } from 'common/interfaces/hierarchy-entities';

pdfjs.GlobalWorkerOptions.workerSrc = new URL(
  'pdfjs-dist/build/pdf.worker.min.mjs',
  import.meta.url,
).toString();
const options = {
  verbosity: pdfjs.VerbosityLevel.ERRORS,
};

interface PDFViewerProps {
  data: IObject;
}

export const PDFViewer = ({
  data,
}: PDFViewerProps) => {
  const [totalpages, setTotalpages] = useState<number>();
  const [selectedPage, setSelectedPage] = useState(1);
  const [numPages, setNumPages] = useState(1);
  const [pdf, setPdf] = useState<string | File | undefined>('');

  const { t } = useTranslation();

  const onDocumentLoadSuccess = ({ numPages }: PDFDocumentProxy) => {
    setTotalpages(numPages);
  };

  useEffect(() => {
    const getPdf = async () => {
      const response = await getRawBlob(data.metadata.blobData.blobNameOriginal);
      const file = (new File([response.data], 'file.pdf', { type: RecongnizebleFileFormats.Pdf }));
      setPdf(file);
    };
    if (data) {
      getPdf();
    }
  }, [data]);

  const renderPages = (numberOfPages: number) => Array.from(new Array(numberOfPages === 0 ? totalpages : numPages), (_, index) => {
    const pageIndex = index + (selectedPage - 1) * numPages;
    if (pageIndex >= (totalpages || 0)) return;
    return (
      <Column key={`page-column-${pageIndex}`}>
        <Page
          key={`page-${pageIndex}`}
          pageIndex={pageIndex}
        />
        <Spacer key={`page-spacer-${pageIndex}`} />
      </Column>
    );
  });

  return (
    <div>
      {totalpages && (
        <Flex justifyContent="center">
          <Pagination
            pagination={{
              rowCount: totalpages,
              selectedPage,
              onSelectPage: (page: number) => setSelectedPage(page),
              rowsPerPage: {
                onChange: (evt: any) => setNumPages(Number(evt.target.value)),
                options: [{
                  label: t(translations.viewer_singlePage),
                  value: 1,
                },
                {
                  label: t(translations.viewer_doublePages),
                  value: 2,
                },
                {
                  label: t(translations.viewer_allPages),
                  value: 0,
                },
                ],
                value: numPages,
              },
            }}
          />
        </Flex>

      )}
      <div>
        <Row>
          <Column scroll>
            <Flex justifyContent="left">
              {pdf !== '' && (
              <Document
                file={pdf}
                onLoadSuccess={onDocumentLoadSuccess}
                options={options}
              >
                { numPages === 2 ? <Row>{renderPages(numPages)}</Row> : renderPages(numPages)}
              </Document>
              )}
            </Flex>
          </Column>
        </Row>
      </div>
    </div>
  );
};
